// ATTENTION-HUB-01A
// Centro de avisos por familia. Las lecturas especializadas permanecen separadas.

import { useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  getMarginCenterCadenceAttention,
  getMarginCenterFollowUpAttention,
  resolveMarginCenterCadenceOccurrence,
} from '../services/marginCenterService';

import type {
  MarginCenterCadenceAttentionItem,
  MarginCenterCadenceAttentionLifecycle,
  MarginCenterCadenceAttentionResolveBody,
} from '../types/cadenceAttention';

export type MarginCenterAttentionCategory =
  | 'cadence'
  | 'follow_up_due';

const PAGE_SIZE = 25;

const DISPOSITIONS: Array<{
  value: MarginCenterCadenceAttentionResolveBody['disposition'];
  label: string;
}> = [
  { value: 'explained', label: 'Situación explicada' },
  { value: 'not_relevant', label: 'No relevante' },
  { value: 'duplicate', label: 'Duplicado' },
  { value: 'discarded', label: 'Descartado' },
  { value: 'insufficient_evidence', label: 'Evidencia insuficiente' },
];

function formatDate(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const dateOnly =
    /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1];

  if (!dateOnly) return value;

  const [year, month, day] =
    dateOnly.split('-').map(Number);

  const date = new Date(
    year,
    month - 1,
    day,
  );

  return Number.isNaN(date.getTime())
    ? dateOnly
    : date.toLocaleDateString(
      'es-ES',
      { dateStyle: 'medium' },
    );
}

function formatNumber(
  value: number | null | undefined,
  maximumFractionDigits = 1,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return value.toLocaleString(
    'es-ES',
    { maximumFractionDigits },
  );
}

function lifecycleLabel(
  lifecycle: MarginCenterCadenceAttentionLifecycle,
): string {
  if (lifecycle === 'active_attention') {
    return 'Activos';
  }

  if (lifecycle === 'historical_unresolved') {
    return 'Históricos sin resolver';
  }

  return 'Archivados';
}

function dispositionLabel(
  value: string | null | undefined,
): string {
  return DISPOSITIONS.find(
    (item) => item.value === value,
  )?.label ?? 'Resuelto';
}

function operationErrorMessage(
  error: unknown,
): string {
  if (!error) {
    return 'No se pudo completar la operación.';
  }

  const candidate = error as {
    response?: {
      data?: {
        detail?: unknown;
      };
    };
    message?: string;
  };

  const detail =
    candidate.response?.data?.detail;

  if (
    typeof detail === 'string'
    && detail.trim()
  ) {
    return detail.trim();
  }

  if (
    typeof candidate.message === 'string'
    && candidate.message.trim()
  ) {
    return candidate.message.trim();
  }

  return 'No se pudo completar la operación.';
}

export default function MarginCenterAttentionCenterDrawer({
  open,
  initialCategory = 'cadence',
  onClose,
  onOpenCadenceRelationship,
  onOpenFollowUp,
}: {
  open: boolean;
  initialCategory?: MarginCenterAttentionCategory;
  onClose: () => void;
  onOpenCadenceRelationship: (
    item: MarginCenterCadenceAttentionItem,
  ) => void;
  onOpenFollowUp: (followUpId: string) => void;
}) {
  const queryClient = useQueryClient();

  const [category, setCategory] =
    useState<MarginCenterAttentionCategory>(initialCategory);

  const [cadenceLifecycle, setCadenceLifecycle] =
    useState<MarginCenterCadenceAttentionLifecycle>('active_attention');

  const [cadenceCursor, setCadenceCursor] =
    useState<string | undefined>(undefined);
  const [cadenceCursorTrail, setCadenceCursorTrail] =
    useState<string[]>([]);
  const [followUpCursor, setFollowUpCursor] =
    useState<string | undefined>(undefined);
  const [followUpCursorTrail, setFollowUpCursorTrail] =
    useState<string[]>([]);

  const [resolveItem, setResolveItem] =
    useState<MarginCenterCadenceAttentionItem | null>(null);
  const [disposition, setDisposition] =
    useState<MarginCenterCadenceAttentionResolveBody['disposition']>('explained');
  const [resolutionType, setResolutionType] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setCategory(initialCategory);
    setCadenceCursor(undefined);
    setCadenceCursorTrail([]);
    setFollowUpCursor(undefined);
    setFollowUpCursorTrail([]);
    setResolveItem(null);
    setSuccessMessage('');
  }, [initialCategory, open]);

  const cadenceSummaryQuery = useQuery({
    queryKey: [
      'margin-center',
      'cadence-attention',
      'attention-center',
      'summary',
    ],
    queryFn: () =>
      getMarginCenterCadenceAttention({
        lifecycle: 'active_attention',
        limit: 1,
      }),
    enabled: open,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const dueSummaryQuery = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'attention',
      'attention-center',
      'summary',
      'due',
    ],
    queryFn: () =>
      getMarginCenterFollowUpAttention({
        state: 'due',
        horizon_days: 7,
        limit: 1,
      }),
    enabled: open,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const cadenceListQuery = useQuery({
    queryKey: [
      'margin-center',
      'cadence-attention',
      'attention-center',
      cadenceLifecycle,
      PAGE_SIZE,
      cadenceCursor,
    ],
    queryFn: () =>
      getMarginCenterCadenceAttention({
        lifecycle: cadenceLifecycle,
        limit: PAGE_SIZE,
        cursor: cadenceCursor,
      }),
    enabled: open && category === 'cadence',
    staleTime: 20 * 1000,
    retry: 1,
  });

  const followUpListQuery = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'attention',
      'attention-center',
      'due',
      PAGE_SIZE,
      followUpCursor,
    ],
    queryFn: () =>
      getMarginCenterFollowUpAttention({
        state: 'due',
        horizon_days: 7,
        limit: PAGE_SIZE,
        cursor: followUpCursor,
      }),
    enabled: open && category === 'follow_up_due',
    staleTime: 20 * 1000,
    retry: 1,
  });

  const resolveMutation = useMutation({
    mutationFn: (item: MarginCenterCadenceAttentionItem) =>
      resolveMarginCenterCadenceOccurrence(
        item.customer_id,
        item.product_id,
        {
          first_outside_p90_date: item.first_outside_p90_date,
          disposition,
          resolution_type: resolutionType.trim() || null,
          conclusion: conclusion.trim(),
        },
      ),
    onSuccess: () => {
      setResolveItem(null);
      setResolutionType('');
      setConclusion('');
      setSuccessMessage(
        'La ocurrencia se ha archivado y ya no pertenece a los avisos pendientes.',
      );
      void queryClient.invalidateQueries({
        queryKey: ['margin-center', 'cadence-attention'],
      });
    },
  });

  const activeCount =
    cadenceSummaryQuery.data?.summary.active_count ?? 0;
  const historicalCount =
    cadenceSummaryQuery.data?.summary.historical_unresolved_count ?? 0;
  const resolvedCount =
    cadenceSummaryQuery.data?.summary.resolved_count ?? 0;
  const dueCount =
    dueSummaryQuery.data?.summary.due_count ?? 0;
  const totalCommercialAttention = activeCount + dueCount;

  const changeCategory = (
    nextCategory: MarginCenterAttentionCategory,
  ) => {
    setCategory(nextCategory);
    setResolveItem(null);
    setSuccessMessage('');
  };

  const changeLifecycle = (
    nextLifecycle: MarginCenterCadenceAttentionLifecycle,
  ) => {
    setCadenceLifecycle(nextLifecycle);
    setCadenceCursor(undefined);
    setCadenceCursorTrail([]);
    setResolveItem(null);
    setSuccessMessage('');
  };

  const nextCadencePage = () => {
    const nextCursor = cadenceListQuery.data?.page.next_cursor;
    if (!nextCursor) return;
    setCadenceCursorTrail((current) => [
      ...current,
      cadenceCursor ?? '',
    ]);
    setCadenceCursor(nextCursor);
  };

  const previousCadencePage = () => {
    if (cadenceCursorTrail.length === 0) return;
    const previous = cadenceCursorTrail[cadenceCursorTrail.length - 1];
    setCadenceCursorTrail((current) => current.slice(0, -1));
    setCadenceCursor(previous || undefined);
  };

  const nextFollowUpPage = () => {
    const nextCursor = followUpListQuery.data?.page.next_cursor;
    if (!nextCursor) return;
    setFollowUpCursorTrail((current) => [
      ...current,
      followUpCursor ?? '',
    ]);
    setFollowUpCursor(nextCursor);
  };

  const previousFollowUpPage = () => {
    if (followUpCursorTrail.length === 0) return;
    const previous = followUpCursorTrail[followUpCursorTrail.length - 1];
    setFollowUpCursorTrail((current) => current.slice(0, -1));
    setFollowUpCursor(previous || undefined);
  };

  const startResolution = (
    item: MarginCenterCadenceAttentionItem,
  ) => {
    setResolveItem(item);
    setDisposition('explained');
    setResolutionType('');
    setConclusion('');
    setSuccessMessage('');
  };

  const submitResolution = () => {
    if (!resolveItem || !conclusion.trim()) return;
    resolveMutation.mutate(resolveItem);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 860, lg: 980 },
          maxWidth: '100vw',
          bgcolor: '#0b1220',
          borderLeft: '1px solid rgba(148,163,184,0.18)',
          backgroundImage: 'none',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          p: { xs: 1.5, md: 2.25 },
          boxSizing: 'border-box',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="overline"
                sx={{ color: '#38bdf8', fontWeight: 850, letterSpacing: 0.9 }}
              >
                Centro de avisos
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: '#f8fafc', fontWeight: 900, lineHeight: 1.2 }}
              >
                Atención comercial
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#94a3b8', mt: 0.35, maxWidth: 680 }}
              >
                Los tipos de aviso comparten una superficie, pero conservan
                su contrato y paginación propios.
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={onClose}
              sx={{ textTransform: 'none' }}
            >
              Cerrar
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
          >
            <Button
              size="small"
              variant={category === 'cadence' ? 'contained' : 'outlined'}
              onClick={() => changeCategory('cadence')}
              sx={{ textTransform: 'none' }}
            >
              Cadencia · {
                cadenceSummaryQuery.isError
                  ? '—'
                  : cadenceSummaryQuery.isPending
                    ? '…'
                    : activeCount
              }
            </Button>
            <Button
              size="small"
              variant={category === 'follow_up_due' ? 'contained' : 'outlined'}
              onClick={() => changeCategory('follow_up_due')}
              sx={{
                textTransform: 'none',
                color:
                  category === 'follow_up_due'
                    ? undefined
                    : dueCount > 0
                      ? '#fbbf24'
                      : undefined,
              }}
            >
              Revisiones vencidas · {
                dueSummaryQuery.isError
                  ? '—'
                  : dueSummaryQuery.isPending
                    ? '…'
                    : dueCount
              }
            </Button>
            <Chip
              size="small"
              variant="outlined"
              label={`Pendientes ahora · ${totalCommercialAttention}`}
            />
          </Stack>

          <Divider sx={{ borderColor: 'rgba(148,163,184,0.14)' }} />

          {category === 'cadence' && (
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.75}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {([
                    'active_attention',
                    'historical_unresolved',
                    'resolved',
                  ] as MarginCenterCadenceAttentionLifecycle[]).map((lifecycle) => {
                    const count =
                      lifecycle === 'active_attention'
                        ? activeCount
                        : lifecycle === 'historical_unresolved'
                          ? historicalCount
                          : resolvedCount;
                    return (
                      <Button
                        key={lifecycle}
                        size="small"
                        variant={cadenceLifecycle === lifecycle ? 'contained' : 'outlined'}
                        onClick={() => changeLifecycle(lifecycle)}
                        sx={{ textTransform: 'none' }}
                      >
                        {lifecycleLabel(lifecycle)} · {count}
                      </Button>
                    );
                  })}
                </Stack>

                {cadenceSummaryQuery.data?.data_cutoff_date && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Datos hasta ${formatDate(
                      cadenceSummaryQuery.data.data_cutoff_date,
                    )}`}
                  />
                )}
              </Stack>

              {cadenceSummaryQuery.data
                && cadenceSummaryQuery.data.data_lag_days
                && cadenceSummaryQuery.data.data_lag_days > 0 && (
                <Alert severity="info">
                  La ausencia de compra se evalúa solo hasta {
                    formatDate(cadenceSummaryQuery.data.data_cutoff_date)
                  }. No se interpreta como inactividad el tiempo posterior al
                  corte de datos.
                </Alert>
              )}

              {successMessage && (
                <Alert severity="success" onClose={() => setSuccessMessage('')}>
                  {successMessage}
                </Alert>
              )}

              {resolveItem && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.4,
                    border: '1px solid rgba(56,189,248,0.24)',
                    bgcolor: 'rgba(8,47,73,0.12)',
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: '#f8fafc', fontWeight: 850 }}
                      >
                        Archivar / resolver esta ocurrencia
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', display: 'block', mt: 0.2 }}
                      >
                        {resolveItem.customer_label} · {resolveItem.product_label}
                        {' · episodio '}
                        {formatDate(resolveItem.first_outside_p90_date)}
                      </Typography>
                    </Box>

                    <TextField
                      select
                      size="small"
                      label="Resultado"
                      value={disposition}
                      onChange={(event) =>
                        setDisposition(
                          event.target.value as MarginCenterCadenceAttentionResolveBody['disposition'],
                        )}
                    >
                      {DISPOSITIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      size="small"
                      label="Motivo comercial"
                      placeholder="Ej. compra puntual, producto sustituido, fin de contrato"
                      value={resolutionType}
                      onChange={(event) => setResolutionType(event.target.value)}
                      inputProps={{ maxLength: 120 }}
                    />

                    <TextField
                      size="small"
                      multiline
                      minRows={3}
                      label="Conclusión humana *"
                      placeholder="Describe por qué esta ocurrencia no requiere seguir apareciendo como pendiente."
                      value={conclusion}
                      onChange={(event) => setConclusion(event.target.value)}
                      inputProps={{ maxLength: 10000 }}
                    />

                    {resolveMutation.isError && (
                      <Alert severity="error">
                        {operationErrorMessage(resolveMutation.error)}
                      </Alert>
                    )}

                    <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={() => setResolveItem(null)}
                        disabled={resolveMutation.isPending}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={submitResolution}
                        disabled={!conclusion.trim() || resolveMutation.isPending}
                        startIcon={
                          resolveMutation.isPending
                            ? <CircularProgress size={14} color="inherit" />
                            : undefined
                        }
                      >
                        Archivar ocurrencia
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}

              {cadenceListQuery.isFetching && !cadenceListQuery.data && (
                <LinearProgress />
              )}

              {cadenceListQuery.isError && (
                <Alert
                  severity="warning"
                  action={(
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => void cadenceListQuery.refetch()}
                    >
                      Reintentar
                    </Button>
                  )}
                >
                  No se pudo consultar esta lista de cadencia.
                </Alert>
              )}

              {cadenceListQuery.data
                && cadenceListQuery.data.items.length === 0 && (
                <Alert severity="info">
                  No hay ocurrencias en {
                    lifecycleLabel(cadenceLifecycle).toLowerCase()
                  }.
                </Alert>
              )}

              {cadenceListQuery.data
                && cadenceListQuery.data.items.length > 0 && (
                <Box
                  sx={{
                    border: '1px solid rgba(148,163,184,0.15)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'grid' },
                      gridTemplateColumns:
                        'minmax(210px,1.25fr) minmax(180px,1fr) minmax(145px,.8fr) minmax(135px,.75fr) 190px',
                      gap: 1,
                      px: 1.2,
                      py: 0.75,
                      bgcolor: 'rgba(15,23,42,0.78)',
                    }}
                  >
                    {[
                      'Relación',
                      'Historial observado',
                      'Cadencia',
                      cadenceLifecycle === 'resolved' ? 'Resolución' : 'Ruptura',
                      'Acciones',
                    ].map((label) => (
                      <Typography
                        key={label}
                        variant="caption"
                        sx={{ color: '#64748b', fontWeight: 800 }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>

                  {cadenceListQuery.data.items.map((item, index) => (
                    <Box
                      key={item.attention_key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md:
                            'minmax(210px,1.25fr) minmax(180px,1fr) minmax(145px,.8fr) minmax(135px,.75fr) 190px',
                        },
                        gap: { xs: 0.65, md: 1 },
                        alignItems: 'center',
                        px: 1.2,
                        py: 0.95,
                        borderTop:
                          index === 0
                            ? 'none'
                            : '1px solid rgba(148,163,184,0.10)',
                        bgcolor: 'rgba(2,6,23,0.18)',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: '#f8fafc', fontWeight: 850 }}
                        >
                          {item.customer_label} · {item.product_label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            display: { xs: 'block', md: 'none' },
                          }}
                        >
                          {formatDate(item.history.first_purchase_date)}
                          {' → '}
                          {formatDate(item.history.last_purchase_date)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                          {formatDate(item.history.first_purchase_date)}
                          {' → '}
                          {formatDate(item.history.last_purchase_date)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {item.history.purchase_year_count}
                          {item.history.purchase_year_count === 1
                            ? ' año con compra'
                            : ' años con compra'}
                          {' · '}
                          {item.history.purchase_days} días de compra
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                          P90 {formatNumber(item.cadence.p90_interval_days)} días
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Última hace {item.cadence.days_since_last_purchase} días
                        </Typography>
                      </Box>

                      <Box>
                        {cadenceLifecycle === 'resolved' ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ color: '#a7f3d0', fontWeight: 800 }}
                            >
                              {dispositionLabel(item.resolution?.disposition)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#64748b', display: 'block' }}
                            >
                              {item.resolution?.resolution_type
                                || formatDate(item.resolution?.resolved_at)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ color: '#7dd3fc', fontWeight: 800 }}
                            >
                              +{formatNumber(item.cadence.days_over_p90)} días
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#64748b', display: 'block' }}
                            >
                              Desde {formatDate(item.first_outside_p90_date)}
                            </Typography>
                          </>
                        )}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.6}
                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            onClose();
                            onOpenCadenceRelationship(item);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Abrir relación
                        </Button>
                        {cadenceLifecycle !== 'resolved' && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => startResolution(item)}
                            sx={{ textTransform: 'none', color: '#fbbf24' }}
                          >
                            Archivar
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}

              {cadenceListQuery.data && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={0.75}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {cadenceListQuery.data.page.returned_count} de {' '}
                    {cadenceListQuery.data.summary.selected_count} · {' '}
                    {lifecycleLabel(cadenceLifecycle)}
                  </Typography>
                  <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={cadenceCursorTrail.length === 0}
                      onClick={previousCadencePage}
                    >
                      Anterior
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={
                        !cadenceListQuery.data.page.has_more
                        || !cadenceListQuery.data.page.next_cursor
                      }
                      onClick={nextCadencePage}
                    >
                      Siguiente
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

          {category === 'follow_up_due' && (
            <Stack spacing={1.25}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#f8fafc', fontWeight: 900 }}
                >
                  Revisiones vencidas
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#94a3b8', display: 'block', mt: 0.2 }}
                >
                  Atención nacida de planificación humana. Abrir un aviso
                  lleva al mismo expediente.
                </Typography>
              </Box>

              {followUpListQuery.isFetching && !followUpListQuery.data && (
                <LinearProgress />
              )}

              {followUpListQuery.isError && (
                <Alert
                  severity="warning"
                  action={(
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => void followUpListQuery.refetch()}
                    >
                      Reintentar
                    </Button>
                  )}
                >
                  No se pudieron consultar las revisiones vencidas.
                </Alert>
              )}

              {followUpListQuery.data
                && followUpListQuery.data.items.length === 0 && (
                <Alert severity="info">No hay revisiones vencidas.</Alert>
              )}

              {followUpListQuery.data
                && followUpListQuery.data.items.length > 0 && (
                <Stack spacing={0.65}>
                  {followUpListQuery.data.items.map((item) => (
                    <Paper
                      key={item.attention_key}
                      elevation={0}
                      sx={{
                        p: 1.1,
                        border: '1px solid rgba(245,158,11,0.18)',
                        bgcolor: 'rgba(120,53,15,0.07)',
                        borderRadius: 1.75,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: '#f8fafc', fontWeight: 850 }}
                          >
                            {item.customer_label || item.customer_id || 'Sin cliente'}
                            {item.product_label || item.product_id
                              ? ` · ${item.product_label || item.product_id}`
                              : ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#cbd5e1', display: 'block', mt: 0.15 }}
                          >
                            {item.next_action || 'Siguiente acción no definida'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b', display: 'block' }}
                          >
                            Revisión {formatDate(item.next_review_at)}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            onClose();
                            onOpenFollowUp(item.follow_up_id);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Abrir expediente
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {followUpListQuery.data && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={0.75}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {followUpListQuery.data.page.returned_count} de {' '}
                    {followUpListQuery.data.summary.due_count} revisiones vencidas
                  </Typography>
                  <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={followUpCursorTrail.length === 0}
                      onClick={previousFollowUpPage}
                    >
                      Anterior
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={
                        !followUpListQuery.data.page.has_more
                        || !followUpListQuery.data.page.next_cursor
                      }
                      onClick={nextFollowUpPage}
                    >
                      Siguiente
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
